import { subCompanyAssignedAeApi, usersManagementApi } from 'app/services/api';
import { isNil } from 'lodash';

import { mapAccountExecutive } from 'utils/accountExecutive';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId, selectMasterSubCompanyId } from 'store/auth/selectors';

// The broker-facing AE card 403s for a caller without the TPO claim (DEV-3647); the skip keeps
// that from tripping the global forbidden page-reload, and the enrichment simply doesn't happen.
const SKIP_FORBIDDEN_CONFIG = { skipResponseInterceptor: { forbiddenError: true } };

export const SUB_COMPANY_ASSIGNED_AE_ACTION_TYPE = createNamedActions('subCompanyAssignedAe', {
  GET_ASSIGNED_AE: 'GET_ASSIGNED_AE',
  SET_ASSIGNED_AE: 'SET_ASSIGNED_AE',
  RESET_STATE: 'RESET_STATE',
});

// The users endpoint carries the email under emailAddress and no fullName, while
// the AE consumers read the eligible-ae shape ({ id, firstName, lastName,
// fullName, email }), so normalize onto that shape here.
const toAssignedAeUser = ({ id, firstName, lastName, fullName, email, emailAddress }) => ({
  id,
  firstName,
  lastName,
  fullName: fullName || [firstName, lastName].filter(Boolean).join(' ') || null,
  email: email ?? emailAddress ?? null,
});

// The assigned-ae endpoints carry only { aeUserId }, while consumers render the
// AE's name/email, so the id resolves to the user record before it reaches the
// store. Resolution is skipped without a companyId (broker-facing callers cannot
// read master-company users) and degrades to the bare id when the lookup fails.
const resolveAssignedAeUser = async ({ companyId, aeUserId }) => {
  if (!aeUserId) {
    return null;
  }

  if (!companyId) {
    return { id: aeUserId };
  }

  try {
    const { data } = await usersManagementApi.getUser({ companyId, id: aeUserId });

    return data ? toAssignedAeUser(data) : { id: aeUserId };
  } catch {
    return { id: aeUserId };
  }
};

// A broker reads its own AE through the self-scoped card endpoint (the only AE identity a TPO
// caller is authorized for), so the name/email survive the master-company lookup being unavailable
// to them. Degrades to the bare id, which is what the lookup-less path returned before.
const resolveOwnAssignedAeUser = async aeUserId => {
  if (!aeUserId) {
    return null;
  }

  try {
    const { data } = await subCompanyAssignedAeApi.getAssignedAeCard({
      config: SKIP_FORBIDDEN_CONFIG,
    });
    const ae = mapAccountExecutive(data);
    // The assignment read is authoritative for WHICH user is the AE; the card only enriches it with
    // a name and email. The two are independent reads, so a card describing a different user (a
    // re-match landing between them, a lagging projection) is dropped rather than shown under the
    // assignment's row.
    const describesAssignedAe = !isNil(ae) && (!ae.userId || ae.userId === aeUserId);

    return describesAssignedAe
      ? { id: aeUserId, fullName: ae.fullName || null, email: ae.email }
      : { id: aeUserId };
  } catch {
    return { id: aeUserId };
  }
};

export const getSubCompanyAssignedAeAction = catchAsyncActionError(
  ({ companyId, subCompanyId }) =>
    async (dispatch, getState) => {
      const { data } = await subCompanyAssignedAeApi.getAssignedAe({ subCompanyId });
      const aeUserId = data?.aeUserId;
      // The card endpoint is scoped to the caller's own sub-company, so it may only answer for it —
      // for any other sub-company it would name a different company's AE.
      const isOwnSubCompany = subCompanyId === selectMasterSubCompanyId(getState());

      return dispatch({
        type: SUB_COMPANY_ASSIGNED_AE_ACTION_TYPE.GET_ASSIGNED_AE,
        payload:
          isNil(companyId) && isOwnSubCompany
            ? await resolveOwnAssignedAeUser(aeUserId)
            : await resolveAssignedAeUser({ companyId, aeUserId }),
      });
    },
);

export const setSubCompanyAssignedAeAction = catchAsyncActionError(
  ({ subCompanyId, aeUserId }) =>
    async (dispatch, getState) => {
      await subCompanyAssignedAeApi.setAssignedAe({ subCompanyId, aeUserId });

      return dispatch({
        type: SUB_COMPANY_ASSIGNED_AE_ACTION_TYPE.SET_ASSIGNED_AE,
        payload: await resolveAssignedAeUser({
          companyId: selectMasterCompanyId(getState()),
          aeUserId,
        }),
      });
    },
);
